// Racine de l’interface : démarrage, connexion, coque, vues, horloge à la seconde, réseau.
import type { Slot } from '../core/schedule';
import type { Store } from '../data/store';
import { mountContacts, type MountedView } from './contacts';
import { openEditor } from './editor';
import { mountLoge, type LogeFocus } from './loge';
import { renderLogin } from './login';
import { openPlanner } from './planner';
import { openLink } from './proposal';
import { mountRdv } from './rdv';
import { mountSettings, openImport } from './settings';
import { renderShell, updateShell, type View } from './shell';
import { brandMark } from './icons';
import { showToast } from './toast';

export function mountApp(root: HTMLElement, store: Store): void {
  let view: View = 'contacts';
  let mounted: MountedView | null = null;
  let mountedKey = '';

  const ctx = {
    openEditor: (id: string | null) => openEditor(store, id ? store.state.contacts.find((c) => c.id === id) ?? null : null),
    openImport: () => openImport(store),
    openSettings: () => navigate('settings'),
    openPlanner: (ids: string[], date?: string) => openPlanner(store, { ids, ...(date ? { date } : {}), onPropose: (slots, meet) => openLink(store, slots, meet) }),
    openLink: (slots: Slot[]) => openLink(store, slots),
  };

  // Retour de Google après le consentement : message, puis retour aux préférences.
  const params = new URLSearchParams(location.search);
  const googleParam = params.get('google');
  if (googleParam) {
    history.replaceState(null, '', location.pathname);
    view = 'settings';
    showToast(googleParam === 'connected'
      ? { id: 0, kind: 'info', text: 'Google Agenda relié.' }
      : { id: 0, kind: 'error', text: `Connexion Google refusée ou interrompue${params.get('reason') ? ' (' + params.get('reason') + ')' : ''}.` });
  }
  // Ouverture directe d’une rubrique (lien d’une notification ou d’un e-mail : /?view=rdv).
  const viewParam = params.get('view');
  if (viewParam === 'contacts' || viewParam === 'rdv' || viewParam === 'loge' || viewParam === 'settings') {
    history.replaceState(null, '', location.pathname);
    view = viewParam;
  }
  // Une notification de demande de rendez-vous pointe directement dessus (/?view=rdv&booking=<id>) : sa
  // décision s’ouvre toute seule, sans chercher la bonne ligne dans la liste. Ne joue qu’une fois.
  let focusBookingId: string | null = params.get('booking');
  // Un e-mail de La Loge au studio ouvre le projet concerné (/?view=loge&project=<id>). Ne joue qu’une fois.
  let logeFocus: LogeFocus | undefined = params.get('project') ? { projectId: params.get('project')! } : undefined;

  function applyTheme(): void {
    const s = store.state;
    if (s.user) document.documentElement.dataset.theme = s.profile.theme;
    else delete document.documentElement.dataset.theme;
  }

  function navigate(v: View): void {
    view = v;
    render();
    window.scrollTo({ top: 0 });
  }

  function render(): void {
    const s = store.state;
    applyTheme();
    if (s.sync.phase === 'boot') {
      if (mountedKey !== 'boot') { root.innerHTML = `<div class="splash" aria-live="polite">${brandMark('splash-mark')}<span>Connexion…</span></div>`; mountedKey = 'boot'; mounted = null; }
      return;
    }
    if (s.sync.phase === 'signed-out' || !s.user) {
      if (mountedKey !== 'login') { renderLogin(root, store); mountedKey = 'login'; mounted = null; }
      return;
    }
    const counts = { contacts: store.active.length, rdv: store.upcomingBookings.length };
    const key = 'shell:' + view;
    if (mountedKey !== key) {
      const main = renderShell(root, store, view, counts);
      root.querySelectorAll<HTMLElement>('[data-view]').forEach((b) => b.addEventListener('click', () => navigate(b.dataset.view as View)));
      mounted = view === 'contacts' ? mountContacts(main, store, ctx) : view === 'rdv' ? mountRdv(main, store, focusBookingId ?? undefined) : view === 'loge' ? mountLoge(main, store, logeFocus) : mountSettings(main, store, ctx);
      mountedKey = key;
      focusBookingId = null;
      logeFocus = undefined;
    } else {
      mounted?.update();
    }
    updateShell(root, store, view, counts);
  }

  store.subscribe(render);
  store.onNotice(showToast);
  render();

  setInterval(() => { if (!document.hidden) mounted?.tick?.(Date.now()); }, 1000);
  window.addEventListener('online', () => store.setOnline(true));
  window.addEventListener('offline', () => store.setOnline(false));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) { store.refreshIfStale(); mounted?.tick?.(Date.now()); } });
  window.addEventListener('focus', () => store.refreshIfStale());
  document.addEventListener('keydown', (e) => {
    if (document.querySelector('dialog[open]') || (e.target as HTMLElement).matches('input, textarea, select') || e.ctrlKey || e.metaKey || e.altKey) return;
    if (!store.state.user) return;
    if (e.key === 'n' && view === 'contacts') { e.preventDefault(); ctx.openEditor(null); }
    if (e.key === '/' && view === 'contacts') { e.preventDefault(); root.querySelector<HTMLInputElement>('[data-search]')?.focus(); }
    if (e.key === 'Escape') mounted?.goLive?.();
  });
}
